import React from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

export interface KPICardProps {
  label: string;
  value: string | number;
  trend?: number;
  trendLabel?: string;
  icon?: React.ReactNode;
  valueFormatter?: (value: number | string) => string;
  loading?: boolean;
}

/**
 * KPI Card Component
 * Displays a key performance indicator with optional trend indicator
 */
export function KPICard({
  label,
  value,
  trend,
  trendLabel,
  icon,
  valueFormatter,
  loading = false,
}: KPICardProps) {
  const formattedValue = valueFormatter
    ? valueFormatter(value)
    : typeof value === 'number'
    ? value.toLocaleString()
    : value;

  const trendDirection = trend && trend > 0 ? 'up' : trend && trend < 0 ? 'down' : 'neutral';
  const showTrend = trend !== undefined && trend !== null && !isNaN(trend);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-32"></div>
          </div>
          {icon && <div className="h-12 w-12 bg-gray-200 rounded-full"></div>}
        </div>
        {showTrend && (
          <div className="mt-4">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{formattedValue}</p>
        </div>
        {icon && (
          <div className="flex-shrink-0 ml-4 p-3 bg-indigo-50 rounded-full text-indigo-600">
            {icon}
          </div>
        )}
      </div>

      {showTrend && (
        <div className="mt-4 flex items-center">
          <span
            className={clsx('inline-flex items-center text-sm font-medium', {
              'text-green-600': trendDirection === 'up',
              'text-red-600': trendDirection === 'down',
              'text-gray-500': trendDirection === 'neutral',
            })}
          >
            {trendDirection === 'up' && (
              <ArrowUpIcon className="h-4 w-4 mr-1" aria-hidden="true" />
            )}
            {trendDirection === 'down' && (
              <ArrowDownIcon className="h-4 w-4 mr-1" aria-hidden="true" />
            )}
            {Math.abs(trend).toFixed(1)}%
          </span>
          {trendLabel && (
            <span className="ml-2 text-sm text-gray-500">{trendLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default KPICard;
